import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '../../utils/languages';
import {
  WEEKDAY_KEYS,
  WEEKDAY_SHORT_LABEL_KEYS,
  copyHoursToAllDays,
  dateToTimeString,
  normalizeWorkHours,
  timeStringToDate,
} from '../../utils/weekdayHours';

// A row has to fit a 360 px phone: narrower fields with less left padding on xs
const PICKER_SX = {
  width: { xs: 108, sm: 120 },
  '& .MuiInputBase-input': { pl: { xs: 1.25, sm: 1.75 } },
};

/**
 * One row per weekday: a chip that toggles the day open/closed, a start-end pair of time
 * pickers and a button that copies the day's times to every day (the common "9-16 every day"
 * is one edit and one click). The value is the work_hours / available_hours JSON
 * ({"mon": {"open": "08:00", "close": "16:00", "closed": false}, ...}); a closed day keeps its
 * times so reopening it restores them.
 */
const WeekdayHoursEditor = ({
  value, onChange, closedLabel, dateLocale, disabled = false, testIdPrefix = 'weekday-hours',
}) => {
  const { t, i18n } = useTranslation();
  const hours = normalizeWorkHours(value);

  const updateDay = (key, field, fieldValue) => {
    onChange({ ...hours, [key]: { ...hours[key], [field]: fieldValue } });
  };

  const onTimeChange = (key, field) => (date) => {
    // Ignore the half-typed values the picker reports while the user types
    if (!date || Number.isNaN(date.getTime())) return;
    updateDay(key, field, dateToTimeString(date));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale || getDateFnsLocale(i18n.language)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {WEEKDAY_KEYS.map((key, index) => {
          const day = hours[key];
          return (
            <Box
              key={key}
              data-testid={`${testIdPrefix}-${key}`}
              sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 }, flexWrap: 'wrap' }}
            >
              <Chip
                label={t(WEEKDAY_SHORT_LABEL_KEYS[index])}
                color={day.closed ? 'default' : 'primary'}
                variant={day.closed ? 'outlined' : 'filled'}
                onClick={disabled ? undefined : () => updateDay(key, 'closed', !day.closed)}
                aria-pressed={!day.closed}
                data-testid={`${testIdPrefix}-${key}-toggle`}
                sx={{
                  width: { xs: 36, sm: 48 },
                  cursor: disabled ? 'default' : 'pointer',
                  '& .MuiChip-label': { px: { xs: 0.5, sm: 1.5 } },
                }}
              />
              {day.closed ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {closedLabel || t('workHours.closed')}
                </Typography>
              ) : (
                <>
                  <TimePicker
                    value={timeStringToDate(day.open)}
                    onChange={onTimeChange(key, 'open')}
                    ampm={false}
                    disabled={disabled}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: PICKER_SX,
                        inputProps: { 'data-testid': `${testIdPrefix}-${key}-open`, 'aria-label': t('workHours.start') },
                      },
                    }}
                  />
                  {/* The two fields read as a range on a phone too; the dash only costs width there */}
                  <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>-</Typography>
                  <TimePicker
                    value={timeStringToDate(day.close)}
                    onChange={onTimeChange(key, 'close')}
                    ampm={false}
                    disabled={disabled}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: PICKER_SX,
                        inputProps: { 'data-testid': `${testIdPrefix}-${key}-close`, 'aria-label': t('workHours.end') },
                      },
                    }}
                  />
                  <Tooltip title={t('workHours.copyToAll')}>
                    <span>
                      <IconButton
                        size="small"
                        sx={{ p: { xs: 0.25, sm: 0.625 } }}
                        onClick={() => onChange(copyHoursToAllDays(hours, key))}
                        disabled={disabled}
                        aria-label={t('workHours.copyToAll')}
                        data-testid={`${testIdPrefix}-${key}-copy`}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </LocalizationProvider>
  );
};

export default WeekdayHoursEditor;
